import React, { Dispatch, SetStateAction } from 'react';
import { ScrollView, StyleSheet, Text } from 'react-native';
import {
  closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import Colors from '../../../constants/Colors';
import Style from '../../../constants/Style';
import FloorItem, { Floor } from './FloorItem';
import PlusIcon from '../../16pxIcons/PlusIcon';
import { logEvent } from '../../LogEvent';
import PressableStyled from '../../PressableStyled';
import { SortableItem } from '../../AssetManagement/AddTemplateModal/components/IssueList/components/SortableItem';

interface FloorListProps {
  floors: Floor[],
  edit: Dispatch<SetStateAction<Floor>>,
  // eslint-disable-next-line no-unused-vars
  remove: (LocationId: string, name: string) => void,
  // eslint-disable-next-line no-unused-vars
  setData: (data: Floor[]) => void,
  addFloor: () => void,
}

const FloorList = ({
  floors, edit, setData, addFloor, remove,
}: FloorListProps) => {
  const renderItem = ({
    item, drag, isDragging, index, attributes, listeners,
  }: any) => (
    <FloorItem
      {...item}
      index={index}
      drag={drag}
      edit={() => {
        logEvent('FloorList', 'edit_floor');
        edit(item);
      }}
      remove={() => {
        logEvent('FloorList', 'delete_floor');
        remove(item.LocationId, item.Name);
      }}
      isActive={isDragging}
      listeners={listeners}
      attributes={attributes}
    />
  );

  return (
    <ScrollView
      style={{ flex: 1, alignSelf: 'stretch' }}
      contentContainerStyle={styles.container}
      testID='edit-floors-modal-floors-list'
    >
      <DndContext
        sensors={useSensors(
          useSensor(PointerSensor, {
            // activationConstraint: { distance: 5 },
          }),
          useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
          }),
        )}
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (over && active.id !== over.id) {
            const oldIndex = floors.findIndex(
              (i) => i.LocationId === active.id,
            );
            const newIndex = floors.findIndex(
              (i) => i.LocationId === over.id,
            );
            const newData = arrayMove(floors, oldIndex, newIndex);
            setData(newData);
          }
        }}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      >
        <SortableContext
          items={floors.map((i) => i.LocationId)}
          strategy={verticalListSortingStrategy}
        >
          {floors.map((item, index) => (
            <SortableItem key={item.LocationId}
              id={item.LocationId}>
              {({ attributes, listeners, isDragging }) => renderItem({
                item,
                index,
                attributes,
                listeners,
                isDragging,
              })}
            </SortableItem>
          ))}
        </SortableContext>
      </DndContext>
      <PressableStyled
        style={[Style.row, styles.addFloorButton]}
        onPress={addFloor}
        testID="map-add-floor-btn"
      >
        <Text style={styles.addFloorText}>Add floor</Text>
        <PlusIcon color={styles.addFloorText.color} />
      </PressableStyled>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 12,
    paddingTop: 32,
    alignSelf: 'stretch',
  },
  stretch: {
    alignSelf: 'stretch',
  },
  draggingStyle: {
    backgroundColor: Colors.light.hereworksBlack100,
    borderRadius: 8,
  },
  addFloorButton: {
    alignSelf: 'stretch',
    marginStart: 18,
    marginEnd: 24,
    borderColor: Colors.light.hereworksBlue500,
    borderWidth: 1,
    borderRadius: 4,
    shadowColor: '#323247',
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
    shadowOffset: { width: 0, height: 2 },
    justifyContent: 'center',
    alignItems: 'center',
    padding: 8,
  },
  addFloorText: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlue500,
    marginEnd: 8,
  },
});

export default FloorList;
